import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import type { DocumentNode } from "graphql";
import type { GraphQLClient, GraphQLResponse } from "graphql-request";

const maxRetries: number = 3;
const RETRY_ON_ERROR_CODES = ["502", "503", "504"];

// A customer entering an unknown, expired or inactive discount code is expected, not a failure.
const isInvalidDiscountCode = (errors: GraphQLResponse["errors"]): boolean =>
    !!errors?.length &&
    errors.every((error) => error.extensions?.code === "DiscountCodeNonApplicable");

const undefinedIfEmpty = <T>(value: T[] | undefined): T[] | undefined => {
    if (!Array.isArray(value) || value.length === 0) {
        return undefined;
    }
    return value;
};

type UpdateMutationFn<TVariables, TData> = (
    variables: TVariables
) => Promise<GraphQLResponse<TData>>;

type UpdateQueryFn<TVariables, TData> = (variables?: TVariables) => Promise<GraphQLResponse<TData>>;

export function updateMutation<TVariables, TData = unknown>(
    execute,
    fn
): UpdateMutationFn<TVariables, TData> {
    const traceID = Math.random().toString(36).substring(7);
    const executeWithRetry = async (variables: TVariables | null, retryCount = 0) => {
        const response = (await execute(variables)) as GraphQLResponse<TData>;
        const { data, errors, status } = response;

        const getMutationName = () => {
            if (errors) {
                return errors?.[0]?.path?.[0] ?? "unknown";
            }
            if (data) {
                return Object.keys(data)?.[0] ?? "unknown";
            }
            return "unknown";
        };

        const mutationName = getMutationName();
        const logPayload = {
            variables,
            execute,
            mutationName,
            retry: retryCount,
            error: errors,
            service: "retry-" + (process.env.NEXT_PUBLIC_HOSTNAME || ""),
            requestHash: traceID,
        };

        const logMessage = `GraphQL Mutation ${mutationName}`;

        const getErrorCode = () => {
            return status?.toString();
        };

        if (
            errors &&
            RETRY_ON_ERROR_CODES.some((code) => getErrorCode() === code) &&
            retryCount < maxRetries
        ) {
            Logger.error(ServiceType.COMMERCE_TOOLS, logMessage, {
                tag: LogTag.ERROR,
                ...logPayload,
            });
            return executeWithRetry(variables, retryCount + 1);
        } else {
            if (isInvalidDiscountCode(errors)) {
                Logger.warn(ServiceType.COMMERCE_TOOLS, logMessage, {
                    tag: LogTag.DISCOUNTS,
                    ...logPayload,
                });
            } else if (errors) {
                Logger.error(ServiceType.COMMERCE_TOOLS, logMessage, {
                    tag: LogTag.ERROR,
                    ...logPayload,
                });
            } else {
                Logger.info(ServiceType.COMMERCE_TOOLS, logMessage, {
                    tag: LogTag.SUCCESS,
                    ...logPayload,
                });
            }
        }

        if (!errors?.length && data?.[mutationName]) {
            fn(data[mutationName]);
        }

        return { errors: undefinedIfEmpty(errors), data, status };
    };
    return (variables: TVariables) => executeWithRetry(variables);
}

export const updateQuery = <TVariables extends object, TData = unknown>(
    client: GraphQLClient,
    fn,
    selector,
    document: DocumentNode,
    filter: any
): UpdateQueryFn<TVariables, TData> => {
    return async function executor(variables?: TVariables) {
        const injectedFilter: Record<string, string | string[]> = filter ?? {};
        if (variables) {
            Object.entries(variables).forEach(([vKey, vValue]) => {
                const token = `$${vKey}`;
                Object.entries(injectedFilter).forEach(([fKey, fValue]) => {
                    if ((fValue as string).indexOf(token) !== -1) {
                        if (Array.isArray(vValue)) {
                            injectedFilter[fKey] = vValue;
                        } else {
                            injectedFilter[fKey] = (injectedFilter[fKey] as string)?.replace(
                                token,
                                vValue
                            );
                        }
                    }
                });
            });
        }

        const res = await client.request<GraphQLResponse<TData>>(document, injectedFilter);
        const { data, errors, status } = res;

        if (!errors) {
            fn(selector(data));
        }

        return { errors: undefinedIfEmpty(errors), data, status };
    };
};
